import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Bot,
  Search,
  Brain,
  BookOpen,
  CheckCircle2,
  Filter,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "./ui/button";

// Search demo with focused animation
const SearchDemo = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [resultCount, setResultCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const searchTerms = [
    "wetenschappelijke claims",
    "bijsluiter referentie",
    "indicatie claims",
  ];

  useEffect(() => {
    let currentIndex = 0;
    const type = async () => {
      const term = searchTerms[0];
      for (let i = 0; i <= term.length; i++) {
        setSearchTerm(term.slice(0, i));
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      setShowFilters(true);
      setResultCount(15);
    };
    type();
  }, []);

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg space-y-4 h-[280px]">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-nn-400 h-5 w-5" />
        <input
          type="text"
          value={searchTerm}
          readOnly
          className="w-full pl-12 pr-4 py-3 border-2 border-nn-100 rounded-lg text-nn-600 font-medium"
          placeholder="Zoek in uitspraken..."
        />
      </div>

      <div
        className={`transition-all duration-500 space-y-3 ${
          showFilters ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
        }`}
      >
        <div className="flex flex-wrap gap-2">
          <span className="px-3 py-2 bg-nn-50 text-nn-600 rounded-lg text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Type uitspraak
          </span>
          <span className="px-3 py-2 bg-nn-teal-50 text-nn-teal-600 rounded-lg text-sm font-medium">
            Betrokken bedrijf
          </span>
          <span className="px-3 py-2 bg-nn-sky-50 text-nn-sky-600 rounded-lg text-sm font-medium">
            Eisen aan reclame
          </span>
        </div>

        {resultCount > 0 && (
          <div className="flex items-center justify-between p-3 bg-nn-gray-50 rounded-lg">
            <span className="text-nn-600 font-medium">
              {resultCount} relevante uitspraken gevonden
            </span>
            <ArrowRight className="h-5 w-5 text-nn-400" />
          </div>
        )}
      </div>
    </div>
  );
};

// Summary demo showing analysis result
const SummaryDemo = () => {
  const [showContent, setShowContent] = useState(false);
  const [showRealContent, setShowRealContent] = useState(false);

  useEffect(() => {
    // First show loading state
    setTimeout(() => setShowContent(true), 500);
    // Then show real content
    setTimeout(() => setShowRealContent(true), 2000);
  }, []);

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg h-[280px]">
      <div
        className={`space-y-4 transition-all duration-700 ${
          showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-nn-teal-600 bg-nn-teal-50 p-2 rounded-lg">
            <Brain className="h-5 w-5" />
            <span className="font-medium">Kernoverweging</span>
          </div>

          {!showRealContent ? (
            // Loading state
            <div className="p-4 bg-nn-gray-50 rounded-lg space-y-3">
              <div className="h-3 w-4/5 bg-nn-200 rounded-full animate-pulse" />
              <div className="h-3 w-3/4 bg-nn-200 rounded-full animate-pulse" />
              <div className="h-3 w-2/3 bg-nn-200 rounded-full animate-pulse" />
            </div>
          ) : (
            // Real content
            <div className="p-4 bg-nn-gray-50 rounded-lg">
              <p className="text-nn-600 leading-relaxed">
                De Codecommissie acht de geschenken niet van betekenis voor de
                uitoefening van het beroep en ziet deze als promotioneel.
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-nn-gray-100 pt-4">
          <div className="flex items-center justify-between text-sm text-nn-gray-600">
            <span>Brondocument</span>
            <span>K24.006</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Lessons demo with focused animation
const LessonsDemo = () => {
  const [activeLessons, setActiveLessons] = useState([]);
  const lessons = [
    "Zorg voor wetenschappelijke onderbouwing bij claims",
    "Check referenties in bijsluiter zorgvuldig",
    "Vermijd suggestieve vergelijkingen",
  ];

  useEffect(() => {
    lessons.forEach((_, index) => {
      setTimeout(() => {
        setActiveLessons((prev) => [...prev, index]);
      }, index * 1000);
    });
  }, []);

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg h-[280px]">
      <div className="space-y-3">
        {lessons.map((lesson, index) => (
          <div
            key={index}
            className={`transform transition-all duration-500 ${
              activeLessons.includes(index)
                ? "translate-x-0 opacity-100"
                : "translate-x-4 opacity-0"
            }`}
          >
            <div className="flex items-start gap-3 p-3 bg-nn-teal-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-nn-teal-500 flex-shrink-0 mt-0.5" />
              <span className="text-nn-teal-700">{lesson}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Hero = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const features = [
    {
      title: "Slim zoeken",
      description: "Zoek op onderwerp, betrokken bedrijf, en type uitspraak",
      demo: <SearchDemo />,
    },
    {
      title: "Snel inzicht",
      description: "Zie de kern van de uitspraak in één oogopslag",
      demo: <SummaryDemo />,
    },
    {
      title: "Praktische lessen",
      description:
        "Destilleer concrete compliance best practices uit de tekst van de uitspraak",
      demo: <LessonsDemo />,
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveSlide((prev) => (prev + 1) % features.length);
        setIsTransitioning(false);
      }, 500);
    }, 8000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative bg-nn-gray-50 overflow-hidden">
      <div className="relative mx-auto min-h-[80vh] flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 items-center py-12 lg:py-16">
          {/* Left column */}
          <div className="space-y-6 lg:space-y-8">
            <div className="space-y-4 lg:space-y-6">
              {/* Badge - keeping it simple */}
              <div className="inline-flex px-3 py-1 rounded-full text-nn-teal-700 bg-nn-teal-50 text-sm font-medium">
                Legal AI Tool
              </div>

              {/* Heading - responsive text size */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-nn-500 leading-tight">
                Compliance Kompas voor Geneesmiddelenreclame
              </h1>

              {/* Description - responsive text size and width */}
              <p className="text-lg sm:text-xl text-nn-600 max-w-full sm:max-w-2xl leading-relaxed">
                Doorzoek de volledige CGR-database op adviezen en klachten.
                Krijg inzicht in de kernoverweging van elke uitspraak.
                Ontwikkeld voor juridische professionals.
              </p>
            </div>

            {/* Button - keeping it accessible */}
            <div className="flex gap-4">
              <Button
                size="lg"
                className="bg-nn-500 text-white hover:bg-nn-600 w-full sm:w-auto"
                onClick={() => {
                  const element = document.querySelector("#zoeken");
                  element?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Start met zoeken
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Footer text - responsive width */}
            <div className="text-nn-600 text-sm w-full sm:w-60">
              Een samenwerking tussen het compliance team van Novo Nordisk en
              Patroon
            </div>
          </div>

          {/* Right column with feature demos */}
          <div className="relative">
            {/* Current feature info */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-nn-500">
                  <h3 className="font-semibold text-lg">
                    {features[activeSlide].title}
                  </h3>
                </div>

                {/* Navigation dots moved here */}
                <div className="flex gap-3">
                  {features.map((_, index) => (
                    <button
                      key={index}
                      className={`h-2 rounded-full transition-all ${
                        activeSlide === index
                          ? "w-8 bg-nn-500"
                          : "w-2 bg-nn-200"
                      }`}
                      onClick={() => setActiveSlide(index)}
                    />
                  ))}
                </div>
              </div>
              <p className="text-nn-600">{features[activeSlide].description}</p>
            </div>

            {/* Demo container */}
            <div
              className={`transition-all duration-500 ${
                isTransitioning
                  ? "opacity-0 translate-y-4"
                  : "opacity-100 translate-y-0"
              }`}
            >
              {features[activeSlide].demo}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
